/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { Switch } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import ButtonsContentTable from '@/components/UI/ButtonsContentTable/ButtonsContentTable';
import Calendar from '@/components/UI/Calendar/Calendar';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import DialogDataSelection from '@/components/UI/DialogDataSelection/DialogDataSelection';
import FormControlLabel from '@/components/UI/FormControlLabel/FormControlLabel';
import InputText from '@/components/UI/InputText/InputText';
import Spinner from '@/components/UI/Spinner/Spinner';
import DialogTableList from '@/components/UI/TableListEditable/DialogTableList/DialogTableList';
import InputCellEditor from '@/components/UI/TableListEditable/InputCellEditor';
import { CELL_EDITOR_TYPES, FILTER_TYPES } from '@/components/UI/TableListEditable/TableListEditableConstants';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import TextAreaCellEditor from '@/components/UI/TableListEditable/TextAreaCellEditor';
import ValueByDIA4ULabel from '@/components/UI/TableListEditable/ValueByDIA4ULabel/ValueByDIA4ULabel';
import { chinaAffected, formYESNO, HARDWARE_WITHOUTPART_TABLE_ID, HARDWARE_WITHPART_TABLE_ID, HELP_AREA, tableDialogRows, tableDialogTitles, TABLE_FIELD_MAX_LENGTH, getPartNumberEditingExtensions, ERROR } from '@/helpers/constants';
import { hasDialogFieldsSynchronized, initDialogUpdateInitialData, langFormFormatter, processEditFieldSynchronization } from '@/helpers/tables-utils';
import { stringToDate, checkDate, isCampaignRDMP, isPDRSCreatedCampaign, isValidQeLevel } from '@/helpers/utils';
import useAppLanguage from '@/hooks/useAppLanguage';
import useCheckDisabled from '@/hooks/useCheckDisabled';
import useDetectLanguage from '@/hooks/useDetectLanguage';
import useError from '@/hooks/useError';
import { usePdrsUnmark } from '@/hooks/usePdrsUnmark';
import useSave from '@/hooks/useSave';
import useToastContext from '@/hooks/useToastContext';
import { setCampaignHardware, setOriginalValues } from '@/redux/Campaign/campaign.action';
import { getCampaignHardware, getCampaignClassification, getOriginalValues, getCampaignInfo } from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import campaignServices from '@/services/campaignServices';
import { CampaignInfo, Classification, AffectedHardware, AffectedHardwareOriginalValues, AffectedPart } from '@/types';
import { ColumnConfig } from '@/types/TableListEditable';
import styles from './Hardware.module.scss';

const COLUMN_DESCRIPTION = 'description';
const COLUMN_ADDITIONAL_INFORMATION = 'additionalInformation';
const COLUMN_VPD = 'vpd';
const COLUMN_BTV_PERSON = 'btvPerson';
const COLUMN_QE_LEVEL = 'qeLevel';

type TableColumnExtension = {
  columnName: string;
  width: number;
  wordWrapEnabled?: boolean;
};

const tableColumnExtensions: TableColumnExtension[] = [
  { columnName: 'numberNew', width: 200 },
  { columnName: COLUMN_DESCRIPTION, width: 230 },
  { columnName: 'numberOld', width: 200 },
  { columnName: 'modelSeries', width: 200 },
  { columnName: 'typeVersions', width: 200 },
  { columnName: 'codeCondition', width: 200 },
  { columnName: 'affectedPlants', width: 200 },
  { columnName: 'qeLevel', width: 200 },
  { columnName: COLUMN_ADDITIONAL_INFORMATION, width: 200 },
  { columnName: 'typeApprovalNumber', width: 260 },
  { columnName: 'manufacturer', width: 200 },
  { columnName: 'zgsOld', width: 200 },
  { columnName: 'zgsNew', width: 200 },
  { columnName: 'perVehicle', width: 200 },
  { columnName: 'qnt', width: 200 },
  { columnName: 'meaning', width: 200 },
  { columnName: COLUMN_BTV_PERSON, width: 300 },
  { columnName: COLUMN_VPD, width: 250, wordWrapEnabled: true },
];

const useStyles = makeStyles(() => ({
  formControlLabel: { alignItems: 'unset', marginLeft: 0 },
  columCoding: { flexBasis: '25%' },
  columFlashing: { flexBasis: '75%' },
  columCodingTable: { flexBasis: '100%', overflow: 'auto' },
  columSwitch: { flexBasis: '25%' },
  columRequest: { flexBasis: '75%' },
  columnSpecial: { width: '50%', marginTop: '-12px', marginBottom: '16px' },
  disabled: {},
}));

const [WITH_CHANGE, WITHOUT_CHANGE] = ['partWithChange', 'partWithoutChange'];
const AFFECTED_HARDWARE = 'affectedHardware';
const PART_AVAILABILITY = 'partAvailability';
const SWITCH_WITH_PART_CHANGE = 'switchWithPartChange';
const SWITCH_WITHOUT_PART_CHANGE = 'switchWithoutPartChange';
const IO_PARTS_AVALAIBLE_FROM = 'ioPartsAvalaibleFrom';
const IO_IDENTIFICATION = 'ioIdentification';

interface HardwareProps {
  campaignHardware: AffectedHardware;
  setHardware: (hardware: AffectedHardware) => void;
  classification: Classification;
  originalValues: AffectedHardwareOriginalValues;
  setOriginValues: (p: { values: AffectedHardware | Record<string, never>; hasChange: boolean }) => void;
  setHelpArea: (area: string) => void;
  subCR: boolean;
  headerLanguage: string;
  campaignInfo: CampaignInfo;
}

interface PartNumberAssignment {
  partId?: string;
  change: boolean;
}

interface Assignments {
  parts: string[];
  campaignId: string;
}
const Hardware = (props: HardwareProps) => {
  const { campaignHardware, setHardware, classification, originalValues, setOriginValues,
    headerLanguage, setHelpArea, subCR, campaignInfo } = props;
  const { t } = useTranslation();
  const { id, version } = useParams<{ id: string; version: string }>();
  const { addToast } = useToastContext();
  const isLoading = !campaignHardware?.id;
  const classes = useStyles();
  const [confirmDialog, setConfirmDialog] = useState<null | string>(null);
  const [selectionPartWithChange, setSelectionPartWithChange] = useState<number[]>([]);
  const [selectionPartWithoutChange, setSelectionPartWithoutChange] = useState<number[]>([]);
  const [openPNList, setOpenPNList] = useState<Assignments | null>(null);
  const [columnsFixed] = useState(['numberNew', COLUMN_DESCRIPTION]);
  const [isOpenPartWithChange, setOpenPartWithChange] = useState(false);
  const [isOpenPartWithoutChange, setOpenPartWithoutChange] = useState(false);
  const [partNumberAssignment, setPartNumberAssignment] = useState<PartNumberAssignment | null>(null);
  const [tableValue, setTableValue] = useState({});
  const checkDisabled = useCheckDisabled(true) || subCR;
  const { handleCriticalError } = useError();
  const { detectLanguage, detectLanguageTranslation } = useDetectLanguage();
  const isRDMP = isCampaignRDMP(campaignInfo?.creationUser.department);
  const isPDRSCampaign = isPDRSCreatedCampaign(campaignInfo);
  const { unmarkPdrsIds } = usePdrsUnmark(campaignInfo?.id);
  const save = useSave('affected-hardware');
  const lang = useAppLanguage();
  const pendingSaveRef = useRef(false);

  useEffect(() => {
    if (pendingSaveRef.current) {
      pendingSaveRef.current = false;
      save();
    }
  });

  const activeEditingStateColumnExtensions = getPartNumberEditingExtensions(isPDRSCampaign);

  const dialogDataColumns = useMemo(() => [
    { name: 'partNumberOld', title: t('software.partNoOld'), wrapText: true },
    { name: 'type', title: t('software.type'), wrapText: true },
    { name: 'zgsOld', title: t('hardware.zgsold'), wrapText: true },
  ], [t]);

  const onCopyOriginValues = (content) => {
    const copyValues = {
      values: content,
      hasChange: false,
    };
    setOriginValues(copyValues);
  };

  const onSetOriginValues = () => {
    const values = {
      ...originalValues,
      hasChange: true,
    };
    setOriginValues(values);
  };

  useEffect(() => {
    setTableValue(tableDialogRows(isRDMP));
  }, []);

  useEffect(() => {
    if (subCR) {
      campaignServices.getSubCampaign(id, 'affected-hardware', version)
        .then((data) => {
          const hardwareData = initDialogUpdateInitialData(data.module) as AffectedHardware;
          onCopyOriginValues(hardwareData);
          setHardware(hardwareData);
        })
        .catch(handleCriticalError);
    } else {
      campaignServices.getCampaignRetry(id, 'affected-hardware', version)
        .then((res) => {
          const hardwareData = initDialogUpdateInitialData(res) as AffectedHardware;
          onCopyOriginValues(hardwareData);
          setHardware(hardwareData);
        })
        .catch(handleCriticalError);
    }
  }, []);

  const updateState = (label, value) => {
    const newState = {
      ...campaignHardware,
      [label]: value,
    };

    onSetOriginValues();
    setHardware(newState);
  };

  const onAffectedHardware = (content) => {
    const data: AffectedPart[] = [];

    for (let i = 0; i < content.length; i++) {
      const dato: AffectedPart = {
        id: i,
        numberNew: content[i].numberNew,
        description: content[i].description,
        modelSeries: content[i].modelSeries,
        typeVersions: content[i].typeVersions,
        codeCondition: content[i].codeCondition,
        affectedPlants: content[i].affectedPlants,
        qeLevel: content[i].qeLevel,
        additionalInformation: content[i].additionalInformation,
        zgsOld: content[i].zgsOld,
        zgsNew: content[i].zgsNew,
        perVehicle: content[i].perVehicle,
        vpd: content[i].vpd,
        dialogSynchronization: content[i]?.dialogSynchronization,
        ...(isRDMP ? { btvPerson: content[i]?.btvPerson } : {}),
      };

      if ([chinaAffected.NO, chinaAffected.NOT_AFFECTED].includes(classification?.affectsChina)) {
        if (campaignHardware?.switchWithPartChange) {
          dato.numberOld = content[i].numberOld;
        }
      } else {
        if (campaignHardware?.switchWithPartChange) {
          dato.numberOld = content[i].numberOld || '';
          dato.typeApprovalNumber = content[i].typeApprovalNumber || '';
          dato.manufacturer = content[i].manufacturer || '';
        }
      }
      data.push(dato);
    }

    return data;
  };

  const handleEditHardwareTables = ({ changed }, table: string) => {
    const changedData = changed[Object.keys(changed)[0]];

    if (changedData === undefined) return;

    const changedRows = campaignHardware[table].map(
      (row, index) => (changed[index] ? processEditFieldSynchronization({ ...row, ...changed[index] }) : row),
    );

    const newState = {
      ...campaignHardware,
      [table]: changedRows,
    };
    onSetOriginValues();
    setHardware(newState);
  };

  const handleCellValueChanged = (table: string) => (event: any) => {
    if (!event.data || !event.colDef.field) return;
    const rowId = event.data.id as number;
    const { field } = event.colDef;
    handleEditHardwareTables({ changed: { [rowId]: { [field]: event.newValue } } }, table);
  };

  const handleUserSelectionChange = (table: string) => ({ rowData, columnName, value }: { rowData: Record<string, unknown>; columnName: string; value: unknown }) => {
    const rowId = rowData.id as number;
    const changedRows = campaignHardware[table].map(
      (row, index) => (index === rowId ? processEditFieldSynchronization({ ...row, [columnName]: value }) : row),
    );
    const newState = {
      ...campaignHardware,
      [table]: changedRows,
    };
    onSetOriginValues();
    setHardware(newState);
  };

  const handleQeLevelBlur = (table: string) => ({ rowData, value }: { rowData: Record<string, unknown>; value: unknown }) => {
    if (!value || isValidQeLevel(value as string)) return;

    const rowId = rowData.id as number;
    const changedRows = campaignHardware[table].map(
      (row, index) => (index === rowId ? { ...row, qeLevel: '' } : row),
    );
    const newState = {
      ...campaignHardware,
      [table]: changedRows,
    };
    onSetOriginValues();
    setHardware(newState);
    addToast(t('hardware.wrongQeLevelFormat'), ERROR);
  };

  const getAssignmentList = () =>
    campaignServices.getAssignments(campaignInfo.id)
      .catch(handleCriticalError);

  const displayForm = (change: boolean) => change ? setOpenPartWithChange(true) : setOpenPartWithoutChange(true);

  const handleNewRowPart = (change: boolean) => {
    if (isPDRSCampaign) {
      setPartNumberAssignment({ change: change });
      getAssignmentList().then((assignments) => {
        setOpenPNList(assignments);
      });
    } else {
      displayForm(change);
    }
  };

  const addNewRow = (data: AffectedPart[], table: string) => {
    let rows: AffectedPart[] = [];
    let newState: AffectedHardware = { ...campaignHardware };

    if (table === WITH_CHANGE) {
      setOpenPartWithChange(false);
      rows = [
        ...campaignHardware.partWithChange,
        ...data,
      ];
      newState = {
        ...campaignHardware,
        partWithChange: rows,
      };
    } else {
      setOpenPartWithoutChange(false);
      rows = [
        ...campaignHardware.partWithoutChange,
        ...data,
      ];
      newState = {
        ...campaignHardware,
        partWithoutChange: rows,
      };
    }

    if (isPDRSCampaign) {
      campaignServices.markAssignments(campaignInfo.id, [data[0].numberOld]).catch((error) => {
        addToast(error.message, ERROR);
      });
    }

    onSetOriginValues();
    setHardware(newState);
    pendingSaveRef.current = true;
  };

  const handleDeleteRow = async (rowsIdx: number[], table: string) => {
    const updatedRows = campaignHardware?.[table]?.filter((tableRow, idx) => !rowsIdx.includes(idx));

    if (isPDRSCampaign) {
      const partNumbersSuccessfullyUnmarked = await unmarkPdrsIds(campaignHardware?.[table], rowsIdx, () => setConfirmDialog(null));
      if (!partNumbersSuccessfullyUnmarked) return;
    }

    if (table === WITH_CHANGE) {
      setSelectionPartWithChange([]);
    } else {
      setSelectionPartWithoutChange([]);
    }

    onSetOriginValues();
    setHardware({ ...campaignHardware, [table]: updatedRows });
    pendingSaveRef.current = true;
  };

  const onDialogConfim = async () => {
    await handleDeleteRow(confirmDialog === WITH_CHANGE ? selectionPartWithChange : selectionPartWithoutChange, confirmDialog!);
    setConfirmDialog(null);
  };

  const handleCopyPartWithChange = () => {
    const rowsCopied = selectionPartWithChange
      .map((elem) => campaignHardware.partWithChange.find((_, index) => index === elem))
      .filter((row): row is AffectedPart => row !== undefined);
    const rows = [
      ...campaignHardware.partWithChange,
    ];
    rowsCopied.forEach((row) => rows.push(row));
    const newState = {
      ...campaignHardware,
      partWithChange: rows,
    };
    onSetOriginValues();
    setHardware(newState);
    setSelectionPartWithChange([]);
  };

  const handleCopyPartWithoutChange = () => {
    const rowsCopied = selectionPartWithoutChange
      .map((elem) => campaignHardware.partWithoutChange.find((_, index) => index === elem))
      .filter((row): row is AffectedPart => row !== undefined);
    const rows = [
      ...campaignHardware.partWithoutChange,
    ];
    rowsCopied.forEach((row) => rows.push(row));
    const newState = {
      ...campaignHardware,
      partWithoutChange: rows,
    };
    onSetOriginValues();
    setHardware(newState);
    setSelectionPartWithoutChange([]);
  };

  const affHardColumnsBaseOne = [
    { name: 'numberNew', title: t('software.partNoNew') },
    { name: COLUMN_DESCRIPTION, title: t('description.label') },
  ];
  const affHardColumnsBaseTwo = [
    { name: 'modelSeries', title: t('hardware.modelSeries') },
    { name: 'typeVersions', title: t('hardware.typeVersions') },
    { name: 'codeCondition', title: t('hardware.codeCondition') },
    { name: 'affectedPlants', title: t('hardware.affectedPlants') },
  ];

  const affHardColumnsBaseThree = [
    { name: 'qeLevel', title: t('hardware.qeLevel') },
    { name: COLUMN_ADDITIONAL_INFORMATION, title: t('hardware.additionalInformation') },
    { name: 'zgsOld', title: t('hardware.zgsold') },
    { name: 'zgsNew', title: t('hardware.zgsnew') },
    { name: 'perVehicle', title: t('hardware.perVehicle') },
    { name: COLUMN_VPD, title: t('hardware.vpd') },
    ...(isRDMP ? [{ name: COLUMN_BTV_PERSON, title: t('dialog.btvPerson') }] : []),
  ];

  let columnsWithPartChange = [
    ...affHardColumnsBaseOne,
    { name: 'numberOld', title: t('software.partNoOld') },
    ...affHardColumnsBaseTwo,
    ...affHardColumnsBaseThree,
  ];

  const columnsWithoutPartChange = [...affHardColumnsBaseOne, ...affHardColumnsBaseTwo, ...affHardColumnsBaseThree];

  const getColumns = (table: string, baseColumns: { name: string; title: string }[]): ColumnConfig[] => {
    const disabledSet = new Set(activeEditingStateColumnExtensions.filter((columnExtension) => columnExtension.editingEnabled === false).map((column) => column.columnName));
    const isEditableTable = !checkDisabled;

    return baseColumns.map((col) => {
      const extendedColumn = tableColumnExtensions.find((extension) => extension.columnName === col.name);
      const isFixed = columnsFixed.includes(col.name);
      const isEditable = isEditableTable && !disabledSet.has(col.name);
      const config: ColumnConfig = {
        ...col,
        flex: extendedColumn?.width ? extendedColumn.width / 200 : 1,
        width: extendedColumn?.width,
        wrapText: extendedColumn?.wordWrapEnabled,
        editable: isEditable,
        ...(isFixed ? { pinned: 'left' as const } : {}),
      };

      if ([COLUMN_DESCRIPTION, COLUMN_ADDITIONAL_INFORMATION].includes(col.name)) {
        config.cellEditor = TextAreaCellEditor;
        config.cellEditorParams = { maxLength: TABLE_FIELD_MAX_LENGTH[col.name] ?? 512 } as any;
        config.wrapText = true;
      } else if (isEditable) {
        config.inputText = { maxLength: TABLE_FIELD_MAX_LENGTH[col.name] ?? 32 };
      }

      if (col.name === COLUMN_VPD) {
        config.cellEditor = CELL_EDITOR_TYPES.SELECT;
        config.cellEditorParams = { values: Object.keys(formYESNO), formatValue: (value: string) => langFormFormatter(value, formYESNO, lang) } as any;
        config.valueFormatter = ({ value }: { value: string }) => langFormFormatter(value, formYESNO, lang);
        config.filterType = FILTER_TYPES.SET;
      }

      if (col.name === COLUMN_BTV_PERSON && isRDMP) {
        config.usersSelection = {
          popupTitle: t('dialog.btvPerson'),
          isOnlyOneUserSelection: true,
          onlyOneUserIsObject: true,
          onChange: handleUserSelectionChange(table),
        };
      }

      if (col.name === COLUMN_QE_LEVEL && isEditable) {
        config.cellEditor = InputCellEditor;
        config.cellEditorParams = { maxLength: TABLE_FIELD_MAX_LENGTH[col.name] ?? 4, placeholder: t('hardware.qeLevelPlaceholder') } as any;
        config.inputText = { maxLength: TABLE_FIELD_MAX_LENGTH[col.name] ?? 4, onBlur: handleQeLevelBlur(table) };
      }

      return config;
    });
  };

  if ((classification?.affectsChina !== chinaAffected.NO && classification?.affectsChina !== chinaAffected.NOT_AFFECTED)
    && campaignHardware?.switchWithPartChange) {
    columnsWithPartChange = [
      ...affHardColumnsBaseOne,
      { name: 'numberOld', title: t('software.partNoOld') },
      ...affHardColumnsBaseTwo,
      { name: 'typeApprovalNumber', title: t('hardware.typeApprovalNumber') },
      { name: 'manufacturer', title: t('hardware.manufacturer') },
      ...affHardColumnsBaseThree,
    ];
  }

  let tableDialogPartWithChange = tableDialogRows(isRDMP).hardwarePartWithChange;
  let tableDialogWithoutPartChange = tableDialogRows(isRDMP).hardwareWithoutPartChange;

  if (classification?.affectsChina === chinaAffected.NO || classification?.affectsChina === chinaAffected.NOT_AFFECTED) {
    if (campaignHardware?.switchWithPartChange) {
      tableDialogPartWithChange = tableDialogRows(isRDMP).hardwarePartWithChange;
    } else if (campaignHardware?.switchWithoutPartChange) {
      tableDialogWithoutPartChange = tableDialogRows(isRDMP).hardwareWithoutPartChange;
    }
  } else if (campaignHardware?.switchWithPartChange) {
    tableDialogPartWithChange = tableDialogRows(isRDMP).hardwareaffectsChina;
  } else if (campaignHardware?.switchWithoutPartChange) {
    tableDialogWithoutPartChange = tableDialogRows(isRDMP, partNumberAssignment?.partId || '').hardwareWithoutPartChange;
  }

  if ((classification?.affectsChina === chinaAffected.NO || classification?.affectsChina === chinaAffected.NOT_AFFECTED)
    && campaignHardware?.id) {
    campaignHardware.partWithoutChange.forEach((element) => {
      delete element.typeApprovalNumber;
      delete element.manufacturer;
    });
    campaignHardware.partWithChange.forEach((element) => {
      delete element.typeApprovalNumber;
      delete element.manufacturer;
    });
  }

  const handleChangeText = (label, value) => {
    const newState = {
      ...campaignHardware,
      partAvailability: {
        ...campaignHardware.partAvailability,
        [label]: label === IO_IDENTIFICATION ? { ...campaignHardware.partAvailability.ioIdentification, [headerLanguage]: value } : value,
      },
    };

    onSetOriginValues();
    setHardware(newState);
  };

  const saveDialogDataInfo = (dataSelected) => {
    const tableDialogPartWithChangeLabel = 'tableDialogPartWithChange';
    const tableDialogWithoutPartChangeLabel = 'tableDialogWithoutPartChange';

    if (partNumberAssignment) {
      setPartNumberAssignment({ ...partNumberAssignment, partId: dataSelected[0].partNumberOld });
      const tableToEdit = partNumberAssignment.change ? tableDialogPartWithChangeLabel : tableDialogWithoutPartChangeLabel;

      const updatedRows: any = (partNumberAssignment.change ? tableDialogPartWithChange : tableDialogWithoutPartChange);

      if (isPDRSCampaign) {
        updatedRows[0].numberOld = dataSelected[0].partNumberOld;
      } else {
        updatedRows[0].numberNew = dataSelected[0].partNumberOld;
      }
      updatedRows[0].zgsOld = dataSelected[0].zgsOld;

      setTableValue({
        [tableToEdit]: updatedRows,
      });

      displayForm(partNumberAssignment.change);
    }
    setOpenPNList(null);
  };

  const onBlur = (name, value) => {
    detectLanguage(value);

    if (originalValues?.hasChange) {
      detectLanguageTranslation(name);
    }
  };

  return (
    <>
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <>
          <div className={styles.Hardware}>
            <AccordionPanel
              title={t('hardware.decisionTypeCampaign')}
              handleClick={() => setHelpArea(HELP_AREA.REQUIREMENTS)}
            >
              <div className={classes.columCoding}>
                <FormControlLabel
                  disabled={checkDisabled}
                  className={classes.formControlLabel}
                  label={t('hardware.reworkWithPartReplacement')}
                  labelPlacement="top"
                  fieldPath={[AFFECTED_HARDWARE, SWITCH_WITH_PART_CHANGE]}
                  control={(
                    <Switch
                      disabled={checkDisabled}
                      checked={campaignHardware?.switchWithPartChange}
                      onChange={(event) => { updateState(event.target.name, event.target.checked); }}
                      name="switchWithPartChange"
                      color="primary"
                    />
                  )}
                />
              </div>
              <div className={classes.columFlashing}>
                <FormControlLabel
                  disabled={checkDisabled}
                  className={classes.formControlLabel}
                  label={t('hardware.reworkWithoutPartReplacement')}
                  labelPlacement="top"
                  fieldPath={[AFFECTED_HARDWARE, SWITCH_WITHOUT_PART_CHANGE]}
                  control={(
                    <Switch
                      disabled={checkDisabled}
                      checked={campaignHardware?.switchWithoutPartChange}
                      onChange={(event) => { updateState(event.target.name, event.target.checked); }}
                      name="switchWithoutPartChange"
                      color="primary"
                    />
                  )}
                />
              </div>
            </AccordionPanel>
            {campaignHardware?.switchWithPartChange && (
              <AccordionPanel
                title={t('hardware.partsAvailability')}
                handleClick={() => setHelpArea(HELP_AREA.PART_AVAIBILITY)}
              >
                {/* Hidden as requested in https://fst-jira.dot.app.corpintra.net/browse/CART-17616 */}
                {/* <div className={classes.columnSpecial}>
                  <FormControlLabel
                    disabled={checkDisabled}
                    className={classes.formControlLabel}
                    label={t('hardware.cocPaperRelated')}
                    labelPlacement="top"
                    fieldPath={[AFFECTED_HARDWARE, PART_AVAILABILITY, COC_PAPER_RELATED]}
                    control={(
                      <Switch
                        disabled={checkDisabled}
                        checked={campaignHardware?.partAvailability?.cocPaperRelated || false}
                        onChange={() => handleChangeText(COC_PAPER_RELATED, !campaignHardware?.partAvailability?.cocPaperRelated)}
                        name="switchShowReplacementText"
                        color="primary"
                      />
                    )}
                  />
                  {campaignHardware?.partAvailability?.cocPaperRelated && (
                    <TooltipComponent title={t('hardware.replacementCoC')}>
                      <span id="coc-text" className={styles.PartsAvailabilityText}>{t('hardware.replacementCoC')}</span>
                    </TooltipComponent>
                  )}
                </div> */}
                <div className={styles.FourColumns}>
                  <Calendar
                    disabled={checkDisabled}
                    id="date-picker-parts-availability-handling"
                    label={t('hardware.calendarIOParts')}
                    handleChange={(date) => checkDate({ date, label: IO_PARTS_AVALAIBLE_FROM, handleChange: handleChangeText })}
                    value={stringToDate(campaignHardware?.partAvailability?.ioPartsAvalaibleFrom)}
                    pickerClass={checkDisabled ? classes.disabled : ''}
                    margin="none"
                    fieldPath={[AFFECTED_HARDWARE, PART_AVAILABILITY, IO_PARTS_AVALAIBLE_FROM]}
                  />
                </div>
                <div className={styles.FourColumns}>
                  <InputText
                    disabled={checkDisabled}
                    maxLength={512}
                    id="io-identification"
                    label={t('hardware.ioIdentification')}
                    handleChange={(ev) => handleChangeText(IO_IDENTIFICATION, ev.target.value)}
                    value={campaignHardware?.partAvailability?.ioIdentification?.[headerLanguage]}
                    onBlur={(ev) => onBlur(IO_IDENTIFICATION, ev.target.value)}
                    fieldPath={[AFFECTED_HARDWARE, PART_AVAILABILITY, IO_IDENTIFICATION]}
                  />
                </div>
              </AccordionPanel>
            )}
            {campaignHardware?.switchWithPartChange && (
              <AccordionPanel
                title={t('hardware.reworkWithPartReplacement')}
                handleClick={() => setHelpArea(WITH_CHANGE)}
                fieldPath={[AFFECTED_HARDWARE, WITH_CHANGE]}
              >
                <div className={classes.columCodingTable}>
                  <ButtonsContentTable
                    onOpenAddPopup={() => handleNewRowPart(true)}
                    onCopyRow={handleCopyPartWithChange}
                    onDeleteRow={() => setConfirmDialog(WITH_CHANGE)}
                    selection={selectionPartWithChange}
                    checkDisabled={checkDisabled}
                    buttonText={isPDRSCampaign ? t('software.importPDRS') : t('software.addPart')}
                  />
                  <TableListEditableNew
                    hasCheckbox={!checkDisabled}
                    list={onAffectedHardware(campaignHardware.partWithChange)}
                    onCellValueChanged={handleCellValueChanged(WITH_CHANGE)}
                    columns={getColumns(WITH_CHANGE, columnsWithPartChange)}
                    selection={selectionPartWithChange}
                    onSelectionChange={(selection) => setSelectionPartWithChange(selection as number[])}
                    filteringStateColumns={[{ columnName: COLUMN_VPD, filteringEnabled: false }]}
                    columnId="id"
                  />
                  {isOpenPartWithChange && (
                    <DialogTableList
                      title="hardware.reworkWithPartReplacement"
                      table={HARDWARE_WITHPART_TABLE_ID}
                      tableDialogRows={(tableValue as any)?.tableDialogPartWithChange || tableDialogPartWithChange}
                      tableDialogTitles={tableDialogTitles}
                      close={() => setOpenPartWithChange(false)}
                      onSave={(data) => addNewRow(data as unknown as AffectedPart[], WITH_CHANGE)}
                    />
                  )}
                  {hasDialogFieldsSynchronized(campaignHardware.partWithChange) && <ValueByDIA4ULabel isTableLegend />}
                </div>
              </AccordionPanel>
            )}
            {campaignHardware.switchWithoutPartChange && (
              <AccordionPanel
                title={t('hardware.reworkWithoutPartReplacement')}
                handleClick={() => setHelpArea(WITHOUT_CHANGE)}
                fieldPath={[AFFECTED_HARDWARE, WITHOUT_CHANGE]}
              >
                <div className={classes.columCodingTable}>
                  <ButtonsContentTable
                    onOpenAddPopup={() => handleNewRowPart(false)}
                    onCopyRow={handleCopyPartWithoutChange}
                    onDeleteRow={() => setConfirmDialog(WITHOUT_CHANGE)}
                    selection={selectionPartWithoutChange}
                    checkDisabled={checkDisabled}
                    buttonText={isPDRSCampaign ? t('software.importPDRS') : t('software.addPart')}
                  />
                  <TableListEditableNew
                    hasCheckbox={!checkDisabled}
                    list={onAffectedHardware(campaignHardware.partWithoutChange)}
                    onCellValueChanged={handleCellValueChanged(WITHOUT_CHANGE)}
                    columns={getColumns(WITHOUT_CHANGE, columnsWithoutPartChange)}
                    selection={selectionPartWithoutChange}
                    onSelectionChange={(selection) => setSelectionPartWithoutChange(selection as number[])}
                    filteringStateColumns={[{ columnName: COLUMN_VPD, filteringEnabled: false }]}
                    columnId="id"
                  />
                  {isOpenPartWithoutChange && (
                    <DialogTableList
                      title="hardware.reworkWithoutPartReplacement"
                      table={HARDWARE_WITHOUTPART_TABLE_ID}
                      tableDialogRows={(tableValue as any)?.tableDialogWithoutPartChange || tableDialogWithoutPartChange}
                      tableDialogTitles={tableDialogTitles}
                      close={() => setOpenPartWithoutChange(false)}
                      onSave={(data) => addNewRow(data as unknown as AffectedPart[], WITHOUT_CHANGE)}
                    />
                  )}
                  {hasDialogFieldsSynchronized(campaignHardware.partWithoutChange) && <ValueByDIA4ULabel isTableLegend />}
                </div>
              </AccordionPanel>
            )}
            {openPNList && openPNList?.parts && (
              <DialogDataSelection
                title={t('dialog.partNumberAssignment')}
                dataToSelect={openPNList?.parts}
                columns={dialogDataColumns}
                columnSort="codeRule"
                onSave={saveDialogDataInfo}
                onClose={() => {
                  setOpenPNList(null);
                  setPartNumberAssignment({ change: false });
                }}
              />
            )}
            {confirmDialog && (
              <ConfirmDialog
                close={() => setConfirmDialog(null)}
                confirm={onDialogConfim}
                title={t('dialog.deleteDataSet.title')}
                message={t('dialog.deleteDataSet.message')}
                closeBtnText={t('dialog.no')}
                confirmBtnText={t('dialog.yes')}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  classification: getCampaignClassification(state),
  campaignHardware: getCampaignHardware(state),
  originalValues: getOriginalValues(state),
  campaignInfo: getCampaignInfo(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
});

const actionCreators = {
  setHardware: setCampaignHardware,
  setOriginValues: setOriginalValues,
  setHelpArea: setAreaHelp,
};

export default connect(mapStateToProps, actionCreators)(Hardware);
